import React, { useEffect, useMemo, useState } from "react";

interface HeroSectionProps {
  tags: { name: string }[];
}

const TYPING_SPEED = 80;
const DELETING_SPEED = 40;
const PAUSE_AFTER_TYPE = 2000;
const PAUSE_AFTER_DELETE = 400;

const HeroSection: React.FC<HeroSectionProps> = ({ tags }) => {
  const techNames = useMemo(() =>
    tags.length > 0
      ? tags.map((t) => `${t.name}.`)
      : ["Elasticsearch.", "Docker.", "Laravel.", "Kubernetes.", "Redis."],
  [tags]);

  const [displayed, setDisplayed] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);

  useEffect(() => {
    if (isPausing) return;
    const current = techNames[currentIndex];

    if (!isDeleting && displayed === current) {
      setIsPausing(true);
      setTimeout(() => {
        setIsDeleting(true);
        setIsPausing(false);
      }, PAUSE_AFTER_TYPE);
      return;
    }

    if (isDeleting && displayed === "") {
      setIsPausing(true);
      setTimeout(() => {
        setIsDeleting(false);
        setCurrentIndex((i) => (i + 1) % techNames.length);
        setIsPausing(false);
      }, PAUSE_AFTER_DELETE);
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayed(
          isDeleting
              ? current.slice(0, displayed.length - 1)
              : current.slice(0, displayed.length + 1)
      );
    }, isDeleting ? DELETING_SPEED : TYPING_SPEED);

    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, isPausing, currentIndex, techNames]);

  return (
      <div className="relative overflow-hidden pt-16 pb-16 lg:pt-24 lg:pb-20">

        {/* 배경 giant word */}
        <span
            aria-hidden="true"
            className="pointer-events-none select-none absolute inset-0 flex items-center justify-center
                   text-[160px] lg:text-[220px] font-bold leading-none tracking-tighter
                   text-neutral-900 dark:text-neutral-100 opacity-[0.03]
                   whitespace-nowrap"
        >
        ENGINEERING
      </span>

        {/* 콘텐츠 */}
        <div className="relative z-10">
          {/* eyebrow */}
          <p className="text-xs font-medium tracking-[0.18em] uppercase text-neutral-400 dark:text-neutral-500 mb-4">
            Coursemos Dev Blog
          </p>

          {/* 카피 + 타이핑 */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-2xl sm:text-3xl lg:text-4xl font-medium text-neutral-400 dark:text-neutral-500 leading-tight whitespace-nowrap">
            We're into
          </span>
            <span className="text-3xl sm:text-4xl lg:text-5xl font-medium text-neutral-900 dark:text-neutral-100 leading-tight min-w-0">
            {displayed}
              <span
                  className="inline-block w-[2px] h-[0.85em] bg-neutral-400 dark:bg-neutral-500 align-middle ml-1"
                  style={{ animation: "blink 1s step-end infinite" }}
              />
          </span>
          </div>
        </div>

        <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
      </div>
  );
};

export default HeroSection;